import React, { useState } from "react";
import { Modal } from "rsuite";
import { ptBR } from "date-fns/locale";

import { MdCalendarMonth, MdGroups, MdErrorOutline } from "react-icons/md";

import DatePicker from "react-datepicker";
import { toast } from "react-toastify";

import api from "../../../../services/axios";
import { FormaLogin } from "../../../../interfaces/Identidade";
import DominiosField from "./DominiosField";
import TenantEntraField, { tenantEntraParaEnvio, tenantEntraValido } from "./TenantEntraField";
import { SIIMP_NATUREZAS, SIIMP_SERVICOS } from "./siimpFiscal";

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";
const labelCls = "text-sm font-semibold text-gray-700 dark:text-gray-300";

const ModalAddClient: React.FC<any> = ({
  openAddClientModal,
  setOpenAddClientModal,
  details,
  updateState,
  jiraProjectList
}) => {
  const [legalName, setLegalName] = useState<string>("");
  const [tradeName, setTradeName] = useState<string>("");
  const [document, setDocument] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [entryDate, setEntryDate] = useState<any>();
  const [startDate, setStartDate] = useState<any>();
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [jiraProject] = useState<string>("")

  // Identidade: como a organização entra e quais domínios de e-mail são dela.
  const [formaLogin, setFormaLogin] = useState<FormaLogin>(0);
  const [dominios, setDominios] = useState<string[]>([]);
  const [dominiosError, setDominiosError] = useState("");
  const [entraTenantId, setEntraTenantId] = useState("");

  // Configuração do report de horas
  const [receivesHoursReport, setReceivesHoursReport] = useState<boolean>(false);
  const [reportFrequency, setReportFrequency] = useState<number>(1); // 1=Semanal,2=Quinzenal,3=Mensal
  const [reportDayOfWeek, setReportDayOfWeek] = useState<number>(1);  // 0=Dom..6=Sáb
  const [reportHour, setReportHour] = useState<number>(8);

  // Dados fiscais / NF
  const [emailNF, setEmailNF] = useState<string>("");
  const [emailBilling, setEmailBilling] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [municipalRegistration, setMunicipalRegistration] = useState<string>("");
  const [paymentTermDays, setPaymentTermDays] = useState<string>("");
  const [billingDay, setBillingDay] = useState<string>("");
  const [siimpNfseServicoId, setSiimpNfseServicoId] = useState<string>("");
  const [siimpNaturezaFiscal, setSiimpNaturezaFiscal] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("");

  const weekDays = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
  const paymentMethods = [
    { value: 1, label: "Boleto" },
    { value: 2, label: "Pix" },
    { value: 3, label: "Transferência / TED" },
    { value: 4, label: "Cartão de crédito" },
    { value: 5, label: "Outro" },
  ];


  const handleClose = () => {
    setOpenAddClientModal(false);
  };

  const handleRegisterClient = async () => {
    const clientData = {
      legalName: legalName,
      tradeName: tradeName,
      document: document,
      // [EmailAddress]/[Phone] no backend aceitam null mas rejeitam "" — campo em branco vira null.
      email: email || null,
      phone: phone || null,
      entryDate: entryDate,
      jiraProjectId: jiraProject ? Number(jiraProject) : null,
      flActive: true,
      receivesHoursReport: receivesHoursReport,
      reportFrequency: receivesHoursReport ? reportFrequency : null,
      reportDayOfWeek: receivesHoursReport ? reportDayOfWeek : null,
      reportHour: receivesHoursReport ? reportHour : null,
      emailNF: emailNF || null,
      emailBilling: emailBilling || null,
      address: address || null,
      municipalRegistration: municipalRegistration || null,
      paymentTermDays: paymentTermDays ? Number(paymentTermDays) : null,
      billingDay: billingDay ? Number(billingDay) : null,
      siimpNfseServicoId: siimpNfseServicoId ? Number(siimpNfseServicoId) : null,
      siimpNaturezaFiscal: siimpNaturezaFiscal ? Number(siimpNaturezaFiscal) : null,
      paymentMethod: paymentMethod ? Number(paymentMethod) : null,
      formaLogin,
      dominios,
      entraTenantId: tenantEntraParaEnvio(entraTenantId),
    };
    const ok = await addClient(clientData);
    if (ok) handleClose();
  };

  const handleDatePicker = (date) => {
    const newData = new Date(date);
    setEntryDate(newData);
    setStartDate(date);
  };

  // Extrai a mensagem real da resposta (ValidationProblemDetails do ASP.NET, string ou fallback).
  function extractApiError(error: any): string {
    const data = error?.response?.data;
    if (typeof data === "string" && data) return data;
    if (data?.errors) return Object.values(data.errors).flat().join(" ");
    if (data?.message || data?.title) return data.message || data.title;
    if (error?.response?.status === 401)
      return "Sessão expirada — faça login novamente.";
    return "Falha de rede ao cadastrar o cliente. Verifique sua conexão.";
  }

  async function addClient(Client: Record<string, unknown>): Promise<boolean> {
    try {
      setLoading(true);
      setErrorMessage("");
      setDominiosError("");
      await api.post("/Client/Clients", Client);
      setLoading(false);
      toast.success("Cliente cadastrado com sucesso", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      updateState();
      return true;
    } catch (error) {
      console.error("Erro ao cadastrar cliente:", error);
      const message = extractApiError(error);
      setLoading(false);
      // 409 = domínio já usado por outro cliente: a mensagem do backend vai embaixo do campo.
      if ((error as any)?.response?.status === 409) setDominiosError(message);
      else setErrorMessage(message);
      toast.error(message, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return false;
    }
  }

  return (
    <>
      <Modal
        backdrop={"static"}
        open={openAddClientModal}
        size={"lg"}
        onClose={handleClose}
      >
        <Modal.Header className="border-b border-gray-100 pb-3 dark:border-gray-700">
          <Modal.Title>
            <div className="flex items-center gap-3">
              <div className="dv-aura flex h-11 w-11 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow">
                <MdGroups className="h-6 w-6" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-base font-bold text-navy-700 dark:text-white">
                  Adicionar novo cliente
                </h2>
                <p className="text-xs font-normal text-gray-700 dark:text-gray-300">
                  Preencha as informações abaixo para cadastrar um cliente
                </p>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="px-1 py-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="add-client-legalName" className={labelCls}>
                  Razão Social <span className="text-red-500">*</span>
                </label>
                <input
                  id="add-client-legalName"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  className={inputCls}
                  placeholder="ex.: Acme Corporation Ltda."
                  required
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="add-client-tradeName" className={labelCls}>
                  Nome Fantasia <span className="text-red-500">*</span>
                </label>
                <input
                  id="add-client-tradeName"
                  value={tradeName}
                  onChange={(e) => setTradeName(e.target.value)}
                  className={inputCls}
                  placeholder="ex.: Acme"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="add-client-document" className={labelCls}>
                  CNPJ <span className="text-red-500">*</span>
                </label>
                <input
                  id="add-client-document"
                  value={document}
                  onChange={(e) => setDocument(e.target.value)}
                  className={`${inputCls} tabular-nums`}
                  placeholder="00.000.000/0000-00"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="add-client-email" className={labelCls}>
                  E-mail
                </label>
                <input
                  id="add-client-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                  placeholder="contato@empresa.com"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="add-client-phone" className={labelCls}>
                  WhatsApp
                </label>
                <input
                  id="add-client-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={inputCls}
                  placeholder="+55 (00) 00000-0000"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="add-client-entryDate" className={labelCls}>
                  Data do contrato <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  id="add-client-entryDate"
                  showIcon
                  selected={startDate}
                  locale={ptBR}
                  icon={<MdCalendarMonth className="mt-1.5 text-base" aria-hidden="true" />}
                  dateFormat="dd-MM-yyyy"
                  onChange={(date) => handleDatePicker(date)}
                  placeholderText="Selecione uma data"
                  className="h-[42px] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </div>
            </div>

            {/* Report de horas */}
            <div className="mt-5 rounded-2xl border border-gray-200 p-4 dark:border-gray-600">
              <div className="flex items-center justify-between gap-4">
                <span>
                  <span className="text-sm font-semibold text-navy-700 dark:text-white">
                    Recebe report de horas
                  </span>
                  <span className="block text-xs text-gray-700 dark:text-gray-300">
                    Envia automaticamente o resumo de horas por e-mail
                  </span>
                </span>
                <button
                  type="button"
                  aria-label="Alternar recebimento de report de horas"
                  aria-pressed={receivesHoursReport}
                  onClick={() => setReceivesHoursReport((v) => !v)}
                  className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-200 ${
                    receivesHoursReport ? "bg-dv-blue-500" : "bg-gray-300 dark:bg-gray-600"
                  }`}
                >
                  <span
                    className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
                      receivesHoursReport ? "translate-x-5" : ""
                    }`}
                  />
                </button>
              </div>

              {receivesHoursReport && (
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="add-client-frequency" className={labelCls}>
                      Frequência
                    </label>
                    <select
                      id="add-client-frequency"
                      value={reportFrequency}
                      onChange={(e) => setReportFrequency(Number(e.target.value))}
                      className={inputCls}
                    >
                      <option value={1}>Semanal</option>
                      <option value={2}>Quinzenal</option>
                      <option value={3}>Mensal</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="add-client-dayOfWeek" className={labelCls}>
                      Dia da semana
                    </label>
                    <select
                      id="add-client-dayOfWeek"
                      value={reportDayOfWeek}
                      onChange={(e) => setReportDayOfWeek(Number(e.target.value))}
                      className={inputCls}
                    >
                      {weekDays.map((d, i) => (
                        <option key={i} value={i}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="add-client-hour" className={labelCls}>
                      Horário
                    </label>
                    <select
                      id="add-client-hour"
                      value={reportHour}
                      onChange={(e) => setReportHour(Number(e.target.value))}
                      className={inputCls}
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Dados fiscais / NF */}
            <div className="mt-5 rounded-2xl border border-gray-200 p-4 dark:border-gray-600">
              <span className="text-sm font-semibold text-navy-700 dark:text-white">
                Dados fiscais / NF
              </span>
              <span className="mb-3 block text-xs text-gray-700 dark:text-gray-300">
                Informações usadas no processo de emissão de nota fiscal e cobrança
              </span>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="add-client-emailNF" className={labelCls}>E-mail para NF</label>
                  <input id="add-client-emailNF" type="email" value={emailNF} onChange={(e) => setEmailNF(e.target.value)} className={inputCls} placeholder="nf@empresa.com" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="add-client-emailBilling" className={labelCls}>E-mail para cobrança</label>
                  <input id="add-client-emailBilling" type="email" value={emailBilling} onChange={(e) => setEmailBilling(e.target.value)} className={inputCls} placeholder="financeiro@empresa.com" />
                </div>
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <label htmlFor="add-client-address" className={labelCls}>Endereço completo</label>
                  <input id="add-client-address" value={address} onChange={(e) => setAddress(e.target.value)} className={inputCls} placeholder="Rua, nº, bairro, cidade/UF, CEP" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="add-client-municipal" className={labelCls}>Inscrição municipal</label>
                  <input id="add-client-municipal" value={municipalRegistration} onChange={(e) => setMunicipalRegistration(e.target.value)} className={inputCls} placeholder="Opcional" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="add-client-paymentMethod" className={labelCls}>Forma de pagamento</label>
                  <select id="add-client-paymentMethod" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={inputCls}>
                    <option value="">Selecione…</option>
                    {paymentMethods.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="add-client-paymentTerm" className={labelCls}>Prazo de pagamento (dias)</label>
                  <input id="add-client-paymentTerm" type="number" min={0} value={paymentTermDays} onChange={(e) => setPaymentTermDays(e.target.value)} className={inputCls} placeholder="ex.: 30" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="add-client-billingDay" className={labelCls}>Dia de cobrança</label>
                  <input id="add-client-billingDay" type="number" min={1} max={31} value={billingDay} onChange={(e) => setBillingDay(e.target.value)} className={inputCls} placeholder="1–31" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="add-client-siimpServico" className={labelCls}>Serviço da NFS-e (SIIMP)</label>
                  <select id="add-client-siimpServico" value={siimpNfseServicoId} onChange={(e) => setSiimpNfseServicoId(e.target.value)} className={inputCls}>
                    <option value="">Padrão</option>
                    {SIIMP_SERVICOS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="add-client-siimpNatureza" className={labelCls}>Natureza fiscal da NFS-e</label>
                  <select id="add-client-siimpNatureza" value={siimpNaturezaFiscal} onChange={(e) => setSiimpNaturezaFiscal(e.target.value)} className={inputCls}>
                    <option value="">Padrão</option>
                    {SIIMP_NATUREZAS.map((n) => (
                      <option key={n.value} value={n.value}>{n.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Identidade */}
            <div className="mt-5 rounded-2xl border border-gray-200 p-4 dark:border-gray-600">
              <span className="text-sm font-semibold text-navy-700 dark:text-white">Acesso ao portal</span>
              <span className="mb-3 block text-xs text-gray-700 dark:text-gray-300">
                Como as pessoas desta organização entram e quais domínios de e-mail são dela
              </span>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="add-client-formaLogin" className={labelCls}>Forma de login</label>
                  <select id="add-client-formaLogin" value={formaLogin} onChange={(e) => setFormaLogin(Number(e.target.value) as FormaLogin)} className={inputCls}>
                    <option value={0}>Senha</option>
                    <option value={1}>Conta Microsoft</option>
                  </select>
                </div>
                <DominiosField id="add-client-dominios" value={dominios} onChange={setDominios} error={dominiosError} labelCls={labelCls} inputCls={inputCls} />
                <TenantEntraField id="add-client-entraTenantId" value={entraTenantId} onChange={setEntraTenantId} labelCls={labelCls} inputCls={inputCls} />
              </div>
            </div>

            {errorMessage && (
              <div className="mt-4 flex items-center gap-2 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-300">
                <MdErrorOutline className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                {errorMessage}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-t border-gray-100 pt-3 dark:border-gray-700">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700"
            >
              Cancelar
            </button>
            {loading ? (
              <button
                type="button"
                className="flex flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white opacity-60"
                disabled
              >
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processando…
              </button>
            ) : (
              <button
                type="submit"
                onClick={handleRegisterClient}
                disabled={
                  legalName.length < 5 ||
                  document.length < 5 ||
                  tradeName.length < 3 ||
                  entryDate == null ||
                  !tenantEntraValido(entraTenantId)
                }
                className="dv-sheen flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Confirmar
              </button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default ModalAddClient;
